/**
 * Detail Delete for an outline record — a line, a root task, or a subtask.
 *
 * Line and task Details each declare this rather than the generic Manage command, because the label
 * must read exactly as the bulk palette names a one-row selection: ⌘K on a focused row and ⌘K on a
 * ticked row are the same action and must say the same words.
 */
import { computed, inject } from '@angular/core';
import { ActivityEntityName, Entity, ItemDeletedResponse } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { ToastService } from '@workbuddy/ui/services';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { BOARD_PANEL_HOST } from '@workbuddy/ui/v2/blocks/entity.board/components/board.panel/board.panel.host';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import type { ActionCommandCapability, ActionCommandContext, ActionCommandDescriptor, SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';

/**
 * What the confirm and the toast say for one record.
 *
 * Every variant keeps the word "Delete": `shift+backspace` resolves this command through
 * `resolveCommandSemanticId`, which matches `\bdelete\b` across id and label. Renaming any of these
 * to "Remove …" would silently take the chord away.
 */
export interface OutlineDeleteCopy {
  /** The ⌘K label and the confirm title. */
  title: string;
  /** The confirm body, given the record's name. */
  question: (name: string) => string;
  /** The success toast heading. */
  toast: string;
  /** Fallback when the delete fails without a server message. */
  failure: string;
}

export interface OutlineDeleteCommandConfig<TRecord extends Entity> {
  entity: ActivityEntityName;
  /** Copy for this record. A function, because a task's wording turns on whether it is a subtask. */
  copy: (record: TRecord | null | undefined) => OutlineDeleteCopy;
}

/** A line: one row, and the server cascades its whole subtree. */
export function outlineLineDeleteCopy(lineNoun = 'service line'): OutlineDeleteCopy {

  return {
    title: `Delete ${lineNoun}`,
    question: name => `Delete '${name}' and all of its tasks and items? This cannot be undone.`,
    toast: `${lineNoun.charAt(0).toUpperCase()}${lineNoun.slice(1)} deleted`,
    failure: `The ${lineNoun} could not be deleted.`,
  };
}

/** A root task takes its subtasks with it; a subtask goes alone. */
export function outlineTaskDeleteCopy(isSubtask: boolean): OutlineDeleteCopy {

  return isSubtask
    ? { title: 'Delete subtask', question: name => `Delete '${name}'? This cannot be undone.`, toast: 'Subtask deleted', failure: 'The task could not be deleted.' }
    : { title: 'Delete task', question: name => `Delete '${name}' and any subtasks? This cannot be undone.`, toast: 'Task deleted', failure: 'The task could not be deleted.' };
}

export function createOutlineDeleteCommand<TRecord extends Entity>(config: OutlineDeleteCommandConfig<TRecord>): ActionCommandCapability<TRecord> {

  const confirm = inject(ConfirmServicePro);
  const toasts = inject(ToastService);
  const entityData = inject(EntityDataService);
  const panel = inject(BOARD_PANEL_HOST, { optional: true });
  const dialogs = inject(EntityDialogs, { optional: true });

  return {
    descriptor: ({ page }: ActionCommandContext<TRecord>) => computed<ActionCommandDescriptor>(() => ({
      id: SemanticCommand.Delete,
      label: config.copy(page.$model()).title,
      iconLeft: 'fa-regular fa-trash-can',
      hidden: !page.$model()?._id || page.$readonly(),
      disabled: page.$saving(),
      pin: 'never',
    })),
    run: async (page: SurfaceContextLike<TRecord>) => {

      const record = page.$model();
      if (!record?._id || page.$readonly()) return;
      const copy = config.copy(record);
      if (!await confirm.confirm(copy.title, copy.question(record.name ?? ''), 'danger')) return;

      try {
        const result = await entityData.getDelete(config.entity).mutateAsync({ id: record._id }) as ItemDeletedResponse;
        if (result?.success !== true) {
          toasts.error('Delete failed', result?.error ?? copy.failure);
          return;
        }
        toasts.success(copy.toast, record.name);
        page.setReadonly(true);
        panel?.refresh('collection');
        panel?.close();
        if (dialogs?.$active()) await dialogs.close();
      } catch (error) {
        toasts.error('Delete failed', error instanceof Error ? error.message : copy.failure);
      }
    },
  };
}
